import {
  apiGetFolders,
  apiCreateFolder,
  apiDeleteFolder,
  apiMoveFolder,
  apiSaveFolder,
  apiRenameFolder
} from '../api/folders';
import { apiGetProject, apiTouchProjectModified } from '../api/projects';
import { debug } from './debug';
import { docMapKey, folderMapKey } from './mapKey';

// -----------------------------
// Folder Helpers
// -----------------------------

export const getFolders = async (projectId) => {
  if (!projectId) return [];
  try {
    const folders = await apiGetFolders(projectId);
    return folders || [];
  } catch (err) {
    console.error('[folderHelpers] getFolders error', err);
    return [];
  }
};

export const createFolder = async (projectId, name, parentId, userId, itemsMap, setItemsMap) => {
  debug('createFolder', { type: 'audit', level: 1 }, { projectId, name, parentId, userId, itemsMap });
  if (!projectId || !userId) return null;
  try {
    const project = await apiGetProject(projectId);
    if (!project) {
      console.warn('[createFolder] user not authorized to create folder in project', projectId, userId);
      return null;
    }
    // compute unique name among all folders in the project
    const base = name || 'New Folder';
    let folderName = base;
    try {
      const existingFolderNames = [];
      if (itemsMap) {
        const iter = itemsMap instanceof Map ? itemsMap.values() : Object.values(itemsMap);
        for (const it of iter) {
          if (it.type === 'folder' && it.name) existingFolderNames.push(it.name);
        }
      }
      const { nextIndexedName } = await import('./projectHelpers');
      folderName = nextIndexedName(base, existingFolderNames);
    } catch (e) {
      folderName = name || 'New Folder';
    }

    const folder = await apiCreateFolder(projectId, folderName, parentId);
    if (!folder) return null;

    const newFolder = {
      id: folder.id,
      name: folderName,  // Use the computed unique name
      parent_id: folder.parent_id ?? parentId,
      type: 'folder',
      created_at: folder.created_at,
      updated_at: folder.updated_at
    };

    if (itemsMap && setItemsMap) {
      if (itemsMap instanceof Map) {
        const newMap = new Map(itemsMap);
        newMap.set(folderMapKey(newFolder.id), newFolder);
        setItemsMap(newMap);
      } else {
        // Assume object
        const newObj = { ...itemsMap, [folderMapKey(newFolder.id)]: newFolder };
        setItemsMap(newObj);
      }
    }

    await apiTouchProjectModified(projectId, userId);

    return newFolder;
  } catch (err) {
    console.error('[folderHelpers] createFolder error', err);
    return null;
  }
};

export const renameFolder = async (folderId, newName, itemsMap, setItemsMap, projectId = null, userId = null) => {
  debug('renameFolder', { type: 'audit', level: 1 }, { folderId, newName, itemsMap });
  if (!folderId || !newName) return null;
  try {
    const updated = await apiRenameFolder(folderId, newName);
    if (!updated) return null;

    if (itemsMap && setItemsMap) {
      if (itemsMap instanceof Map) {
        const cur = itemsMap.get(folderMapKey(folderId));
        const newMap = new Map(itemsMap);
        newMap.set(folderMapKey(folderId), { ...cur, name: newName });
        setItemsMap(newMap);
      } else {
        const cur = itemsMap[folderMapKey(folderId)];
        const newObj = { ...itemsMap, [folderMapKey(folderId)]: { ...cur, name: newName } };
        setItemsMap(newObj);
      }
    }

    if (projectId && userId) {
      await apiTouchProjectModified(projectId, userId);
    }

    return updated;
  } catch (err) {
    console.error('[folderHelpers] renameFolder error', err);
    return null;
  }
};

export const deleteFolder = async (folderId, itemsMap, setItemsMap, projectId = null, userId = null) => {
  debug('deleteFolder', { type: 'audit', level: 1 }, { folderId, itemsMap });
  if (!folderId) return null;
  try {
    const deleted = await apiDeleteFolder(folderId);
    if (!deleted) return null;

    if (itemsMap && setItemsMap) {
      if (itemsMap instanceof Map) {
        const newMap = new Map(itemsMap);
        const removeFolderIds = [folderId];
        const removeDocIds = [];
        const collectChildren = (id) => {
          [...newMap.values()].forEach(item => {
            if (item.parent_id !== id) return;

            if (item.type === 'folder') {
              removeFolderIds.push(item.id);
              collectChildren(item.id);
              return;
            }

            if (item.type === 'doc') removeDocIds.push(item.id);
          });
        };
        collectChildren(folderId);

        removeFolderIds.forEach(id => newMap.delete(folderMapKey(id)));
        removeDocIds.forEach(id => newMap.delete(docMapKey(id)));
        setItemsMap(newMap);
      } else {
        const newObj = { ...itemsMap };
        const removeFolderIds = [folderId];
        const removeDocIds = [];
        const collectChildren = (id) => {
          Object.values(newObj).forEach(item => {
            if (item.parent_id !== id) return;

            if (item.type === 'folder') {
              removeFolderIds.push(item.id);
              collectChildren(item.id);
              return;
            }

            if (item.type === 'doc') removeDocIds.push(item.id);
          });
        };
        collectChildren(folderId);

        removeFolderIds.forEach(id => delete newObj[folderMapKey(id)]);
        removeDocIds.forEach(id => delete newObj[docMapKey(id)]);
        setItemsMap(newObj);
      }
    }

    if (projectId && userId) {
      await apiTouchProjectModified(projectId, userId);
    }

    return true;
  } catch (err) {
    console.error('[folderHelpers] deleteFolder error', err);
    return null;
  }
};

export const moveFolder = async (folderId, newParentId, itemsMap, setItemsMap, projectId = null, userId = null) => {
  debug('moveFolder', { type: 'audit', level: 1 }, { folderId, newParentId, itemsMap });
  if (!folderId) return null;
  try {
    const moved = await apiMoveFolder(folderId, newParentId);
    if (!moved) return null;

    if (itemsMap && setItemsMap) {
      if (itemsMap instanceof Map) {
        const cur = itemsMap.get(folderMapKey(folderId));
        const newMap = new Map(itemsMap);
        newMap.set(folderMapKey(folderId), { ...cur, parent_id: newParentId });
        setItemsMap(newMap);
      } else {
        const cur = itemsMap[folderMapKey(folderId)];
        const newObj = { ...itemsMap, [folderMapKey(folderId)]: { ...cur, parent_id: newParentId } };
        setItemsMap(newObj);
      }
    }

    if (projectId && userId) {
      await apiTouchProjectModified(projectId, userId);
    }

    return moved;
  } catch (err) {
    console.error('[folderHelpers] moveFolder error', err);
    return null;
  }
};

export const saveFolder = async (folderId, updates, itemsMap, setItemsMap) => {
  debug('saveFolder', { type: 'audit', level: 3 }, { folderId, updates, itemsMap });
  if (!folderId || !updates) return null;
  try {
    const saved = await apiSaveFolder(folderId, updates);
    if (!saved) return null;

    if (itemsMap && setItemsMap) {
      if (itemsMap instanceof Map) {
        const cur = itemsMap.get(folderMapKey(folderId));
        const newMap = new Map(itemsMap);
        newMap.set(folderMapKey(folderId), { ...cur, ...updates });
        setItemsMap(newMap);
      } else {
        const cur = itemsMap[folderMapKey(folderId)];
        const newObj = { ...itemsMap, [folderMapKey(folderId)]: { ...cur, ...updates } };
        setItemsMap(newObj);
      }
    }

    return saved;
  } catch (err) {
    console.error('[folderHelpers] saveFolder error', err);
    return null;
  }
};
